import React from "react";
import { Link } from "react-router-dom";
import { Quote } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import InfoModal from "../InfoModal";
import PricingTable from "./PricingTable";
import BookServiceButton from "./BookServiceButton";
import { individualIntro, formula1, formula2, formula3 } from "../../data/servicesContent";

const FormulaModalContent = ({ formula }) => (
  <>
    <p>{formula.intro}</p>

    {formula.details && (
      <ul className="list-disc list-inside space-y-2">
        {formula.details.map((detail, index) => (
          <li key={index}>{detail}</li>
        ))}
      </ul>
    )}

    {formula.frequency && <p>{formula.frequency}</p>}

    {formula.optionsHeading && (
      <div>
        <p className="font-semibold text-[#21167D] mb-2">{formula.optionsHeading}</p>
        <ul className="list-disc list-inside space-y-2">
          {formula.options.map((option, index) => (
            <li key={index}>{option}</li>
          ))}
        </ul>
      </div>
    )}

    {formula.cases && (
      <div className="space-y-4">
        {formula.cases.map((c, index) => (
          <div key={index}>
            <p className="font-semibold text-[#21167D] mb-1">{c.title}</p>
            <p>{c.text}</p>
          </div>
        ))}
      </div>
    )}

    {formula.servicesHeading && (
      <div>
        <p className="font-semibold text-[#21167D] mb-2">{formula.servicesHeading}</p>
        <ul className="list-disc list-inside space-y-2">
          {formula.services.map((s, index) => (
            <li key={index}>{s}</li>
          ))}
        </ul>
      </div>
    )}

    {formula.pricingHeading && (
      <div>
        <p className="font-semibold text-[#21167D] mb-2">{formula.pricingHeading}</p>
        <ul className="list-disc list-inside space-y-2">
          {formula.pricing.map((p, index) => (
            <li key={index}>{p}</li>
          ))}
        </ul>
      </div>
    )}

    {formula.notes && (
      <div>
        <p className="font-semibold text-[#21167D] mb-2">{formula.notesHeading}</p>
        <ul className="list-disc list-inside space-y-2">
          {formula.notes.map((n, index) => (
            <li key={index}>{n}</li>
          ))}
        </ul>
      </div>
    )}

    {formula.image && (
      <img
        src={formula.image.src}
        alt={formula.image.alt}
        loading="lazy"
        className="w-full h-56 object-cover rounded-xl"
      />
    )}

    {formula.note && <p className="text-sm italic">{formula.note}</p>}

    {formula.quote && (
      <blockquote className="flex gap-3 p-4 bg-[#21167D]/5 rounded-xl border-l-4 border-[#EAA93D]">
        <Quote className="w-5 h-5 text-[#DCB071] flex-shrink-0 mt-1" />
        <span className="font-accent italic text-[#21167D]">« {formula.quote} »</span>
      </blockquote>
    )}
  </>
);

const IndividualSection = () => {
  const formulas = [formula1, formula2, formula3];

  return (
    <div className="space-y-16">
      {/* Bannière + intro (section H) */}
      <div>
        <div className="relative w-full h-40 md:h-64 rounded-2xl overflow-hidden shadow-md mb-8">
          <img
            src={individualIntro.banner.src}
            alt={individualIntro.banner.alt}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#21167D]/30 flex items-center justify-center px-4">
            <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#F5F3EB] text-center drop-shadow">
              {individualIntro.heading}
            </h2>
          </div>
        </div>
        <p className="font-accent italic text-xl text-[#4A4458] text-center max-w-2xl mx-auto mb-2">
          {individualIntro.text}
        </p>
        <p className="text-sm text-[#4A4458]/80 text-center max-w-2xl mx-auto">
          {individualIntro.note}
        </p>
      </div>

      {/* Cartes formules */}
      <div className="grid md:grid-cols-3 gap-8">
        {formulas.map((formula) => (
          <Card key={formula.id} className="flex flex-col bg-[#FDFBF5] hover:shadow-xl transition-shadow">
            <CardContent className="p-6 flex flex-col flex-1">
              <h3 className="font-accent font-semibold text-xl text-[#21167D] mb-3">{formula.title}</h3>
              <p className="text-[#4A4458] mb-4 leading-relaxed flex-1">{formula.summary}</p>
              <p className="text-sm font-semibold text-[#21167D] mb-6">{formula.price}</p>
              <div className="space-y-3">
                <InfoModal
                  triggerLabel="En savoir plus"
                  triggerClassName="w-full"
                  title={formula.title}
                  image={formula.image}
                >
                  <FormulaModalContent formula={formula} />
                </InfoModal>
                {formula.bookingOptions.map((option) => (
                  <BookServiceButton
                    key={option.serviceId}
                    serviceId={option.serviceId}
                    label={option.label}
                    className="w-full"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tableau comparatif des tarifs (section L) */}
      <div>
        <h3 className="font-display text-2xl font-semibold text-[#21167D] mb-6 text-center">
          Tableau comparatif des tarifs
        </h3>
        <PricingTable />
      </div>

      <div className="text-center">
        <Link to="/rendez-vous">
          <Button size="lg" className="shadow-lg">
            Prendre rendez-vous
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default IndividualSection;
